// BDSJEA-441/443: modelo de selección por niveles de la Importación Masiva.
// Funciones PURAS (sin React): cascada, bloqueo de casillas, resumen previo,
// detalle por nivel para la confirmación y armado de los parámetros de envío.
//
// Contrato congelado: docs/importacion-niveles/contrato.md del repo de services
// (contratoNiveles = 2: v2 suma la opción aparte «recalcular scoring», BDSJEA-454).
// La cascada es la MISMA que valida el backend
// (`normalizarNiveles`, fuente de verdad) y el migrador; acá se replica solo para
// la UX de las casillas. Se valida contra niveles_cascada.json (fixture
// compartido) en los tests.

// Ids canónicos (ASCII, minúsculas, sin tilde) en orden canónico.
export const NIVELES = Object.freeze([
  "productores",
  "establecimientos",
  "siembras",
  "ganaderia",
  "tambo",
])

export const ACTIVIDADES = Object.freeze(["siembras", "ganaderia", "tambo"])

export const ETIQUETAS_NIVEL = Object.freeze({
  productores: "Productores",
  establecimientos: "Establecimientos",
  siembras: "Siembras",
  ganaderia: "Ganadería",
  tambo: "Tambo",
})

// Qué implica marcar cada nivel (contrato 3.2).
const IMPLICA = Object.freeze({
  productores: Object.freeze([
    "establecimientos",
    "siembras",
    "ganaderia",
    "tambo",
  ]),
  establecimientos: Object.freeze(["siembras", "ganaderia", "tambo"]),
})

// Estado inicial del modal (decisión Q1): Productores marcado = corrida completa
// con actividades.
export const SELECCION_POR_DEFECTO = Object.freeze(["productores"])

export const MENSAJE_NIVELES_NO_DISPONIBLE =
  "La importación por niveles no está disponible todavía en este ambiente. Avisá al equipo técnico."

const esNivel = n => NIVELES.includes(n)

// Orden canónico, sin repetidos ni ids desconocidos.
const ordenar = conjunto => NIVELES.filter(n => conjunto.has(n))

/**
 * Selección efectiva: aplica la cascada a lo que marcó el usuario (idempotente e
 * independiente del orden de entrada). Devuelve ids en orden canónico.
 */
export const efectivaDe = marcadas => {
  const lista = Array.isArray(marcadas) ? marcadas : []
  const s = new Set(lista.filter(esNivel))
  if (s.has("productores")) IMPLICA.productores.forEach(n => s.add(n))
  if (s.has("establecimientos")) IMPLICA.establecimientos.forEach(n => s.add(n))
  return ordenar(s)
}

/**
 * Casillas bloqueadas: las implicadas por un ancestro marcado. Una casilla
 * bloqueada está marcada y no responde al clic.
 */
export const bloqueadasDe = marcadas => {
  const lista = Array.isArray(marcadas) ? marcadas : []
  const s = new Set()
  Object.keys(IMPLICA).forEach(raiz => {
    if (lista.includes(raiz)) IMPLICA[raiz].forEach(n => s.add(n))
  })
  return ordenar(s)
}

/** Ancestro (el más alto marcado) que bloquea a `nivel`, o null. */
export const bloqueadoPor = (marcadas, nivel) => {
  const lista = Array.isArray(marcadas) ? marcadas : []
  const raiz = Object.keys(IMPLICA).find(
    r => lista.includes(r) && IMPLICA[r].includes(nivel),
  )
  return raiz || null
}

/**
 * Alterna una casilla sobre las marcas EXPLÍCITAS. Un clic sobre una casilla
 * bloqueada se ignora. Desmarcar un ancestro restaura la marca explícita previa
 * (no deja marcado lo que el usuario nunca tocó).
 */
export const alternarNivel = (marcadas, nivel) => {
  const lista = Array.isArray(marcadas) ? marcadas : []
  if (!esNivel(nivel)) return ordenar(new Set(lista.filter(esNivel)))
  if (bloqueadasDe(lista).includes(nivel)) {
    return ordenar(new Set(lista.filter(esNivel)))
  }
  const s = new Set(lista.filter(esNivel))
  if (s.has(nivel)) s.delete(nivel)
  else s.add(nivel)
  return ordenar(s)
}

/**
 * ¿El selector por niveles se ofrece? Solo si GET /disponibilidad declara
 * `niveles === true` (contrato 5.3); cualquier otra cosa (propiedad ausente,
 * `false`, texto, respuesta vacía o rara) es "no": fail-closed.
 */
export const capacidadNivelesDe = disponibilidad =>
  disponibilidad != null &&
  typeof disponibilidad === "object" &&
  disponibilidad.niveles === true

/**
 * BDSJEA-454: ¿se ofrece «Recalcular scoring al finalizar»? Solo si
 * GET /disponibilidad declara `scoring === true` Y `niveles === true`
 * (fail-closed: propiedad ausente, `false` o respuesta rara = no; un micro viejo
 * no declara `scoring` y el modal se degrada sin error).
 */
export const capacidadScoringDe = disponibilidad =>
  capacidadNivelesDe(disponibilidad) && disponibilidad.scoring === true

/** Las opciones solo-Productores rigen únicamente con Productores efectivo. */
export const opcionesProductoresHabilitadas = efectiva =>
  Array.isArray(efectiva) && efectiva.includes("productores")

/** true si la selección explícita es la de arranque (Productores solo). */
export const esSeleccionPorDefecto = marcadas =>
  Array.isArray(marcadas) &&
  marcadas.length === 1 &&
  marcadas[0] === SELECCION_POR_DEFECTO[0]

/**
 * ¿Qué se manda al lanzar? Fail-closed (contrato 5.3 y 9.5):
 * - con la capacidad `niveles` del backend: siempre `niveles` (si hay algo marcado);
 * - sin la capacidad: la llamada histórica (sin `niveles`), salvo que hubiera una
 *   selección que ya no se puede respetar: entonces se bloquea sin llamar a la API.
 */
export const decidirEnvio = ({ capacidadNiveles, marcadas }) => {
  const efectiva = efectivaDe(marcadas)
  if (capacidadNiveles === true) {
    return efectiva.length > 0
      ? { tipo: "niveles", niveles: efectiva }
      : { tipo: "vacio" }
  }
  if (esSeleccionPorDefecto(marcadas)) return { tipo: "historico" }
  return { tipo: "bloqueado", mensaje: MENSAJE_NIVELES_NO_DISPONIBLE }
}

/**
 * Parámetros para `ejecutarMigracion` (contrato 9.1). Las opciones solo-Productores
 * (validarCuit, detectarDuplicados, excluirArca, limpieza completa) solo viajan
 * con Productores efectivo; sin él quedan en false y no se envían.
 * Con `niveles: null` (envío histórico) el armado es el de siempre.
 */
export const construirParametros = ({
  niveles = null,
  validarCuit = false,
  detectarDuplicados = false,
  excluirArca = false,
  recalcularScoring = false,
  limpiezaActiva = false,
  confirmacionLimpieza = "",
}) => {
  const conProductores = niveles == null || niveles.includes("productores")
  const params = conProductores
    ? { validarCuit, detectarDuplicados }
    : { validarCuit: false, detectarDuplicados: false }
  if (conProductores && limpiezaActiva) {
    params.limpiezaCompleta = true
    params.confirmacionLimpieza = confirmacionLimpieza
  }
  if (conProductores && excluirArca) params.excluirArca = true
  // BDSJEA-454: opción aparte (no es un nivel), opt-in; solo viaja si es true y
  // hay selección por niveles (con la llamada histórica no se manda).
  if (niveles != null && recalcularScoring === true) {
    params.recalcularScoring = true
  }
  if (niveles != null) params.niveles = niveles.join(",")
  return params
}

// ---------------------------------------------------------------------------
// Recalcular scoring (BDSJEA-454, contrato 14)
// ---------------------------------------------------------------------------

export const ETIQUETA_RECALCULAR_SCORING = "Recalcular scoring al finalizar"

export const DESCRIPCION_RECALCULAR_SCORING =
  "Tildado: al terminar la importación se recalcula el scoring de los productores de esta corrida. Sin tildar: no se recalcula nada."

// Advertencias de la confirmación (una línea por punto, en español).
export const AVISOS_RECALCULAR_SCORING = Object.freeze([
  "El recálculo corre al final de la importación y puede tardar varias horas.",
  "Solo se recalculan los productores de esta corrida.",
  "El scoring nuevo reemplaza al vigente: en los productores que ya existían o se actualizaron (YA_EXISTIA / ACTUALIZADO) se pierde la revisión manual del analista.",
  "Si el recálculo se corta, el equipo técnico continúa con los pendientes. Volver a lanzar la importación desde esta pantalla reimporta todo.",
])

// ---------------------------------------------------------------------------
// Resumen previo (contrato 9.2)
// ---------------------------------------------------------------------------

const listaActividades = efectiva =>
  ACTIVIDADES.filter(a => efectiva.includes(a))
    .map(a => ETIQUETAS_NIVEL[a])
    .join(", ")

/** Raíz de la selección: el nivel más alto efectivo. */
export const raizDe = efectiva => {
  if (!Array.isArray(efectiva) || efectiva.length === 0) return null
  if (efectiva.includes("productores")) return "productores"
  if (efectiva.includes("establecimientos")) return "establecimientos"
  return "actividades"
}

/**
 * Texto fijo, derivado de la selección efectiva, que explica qué va a pasar.
 * `excluirArca` solo cambia la mención a ARCA en la corrida completa.
 */
export const textoResumenPrevio = (efectiva, { excluirArca = false } = {}) => {
  const raiz = raizDe(efectiva)
  if (raiz === "productores") {
    return `Corrida completa: se borran y vuelven a importar los productores y establecimientos migrados${
      excluirArca ? "" : ", se sincroniza con ARCA"
    } y se importan Siembras, Ganadería y Tambo. Esta selección equivale a la corrida completa e incluye las actividades.`
  }
  if (raiz === "establecimientos") {
    return "Se borran y vuelven a importar los establecimientos migrados y, por dependencia, Siembras, Ganadería y Tambo. No se tocan los productores ni ARCA."
  }
  if (raiz === "actividades") {
    return `Se borran y vuelven a importar solo las filas migradas de: ${listaActividades(
      efectiva,
    )}. No se tocan productores, establecimientos ni las demás actividades.`
  }
  return "Elegí al menos un nivel para importar."
}

// ---------------------------------------------------------------------------
// Detalle por nivel (confirmación, contrato 9.4 / tabla 3.3)
// ---------------------------------------------------------------------------

// BDSJEA-462 (decisión del developer, 26-10): los lotes se recrean al reimportar establecimientos; las actividades no los tocan.
// BDSJEA-463: cada fila de destino da un lote propio y uno arrendado; la misma corrida reimporta las actividades y las siembras quedan sin lote.
export const AVISO_LOTES_EDITADOS =
  "Reimportar establecimientos recrea los lotes desde el origen (un lote propio y uno arrendado por cada fila de destino) y descarta las ediciones manuales de superficie, destino y tenencia y los lotes creados a mano: cambian la cantidad y el nombre de los lotes. La misma corrida reimporta Siembras, Ganadería y Tambo (cascada) y las siembras quedan sin lote."

export const AVISO_LOTES_ACTIVIDADES =
  "Reimportar actividades no toca los lotes (ni los importados ni los editados a mano)."

export const AVISO_LO_MANUAL =
  "También se borra lo cargado a mano que cuelga de lo migrado (p. ej. siembras de un establecimiento migrado)."

const TEXTO_ACTIVIDAD = Object.freeze({
  siembras: {
    borra:
      "Solo las siembras importadas desde el origen y sus costos, márgenes, ventas y documentos.",
    reimporta: "Siembras.",
  },
  ganaderia: {
    borra: "Solo el stock ganadero importado desde el origen.",
    reimporta: "Ganadería.",
  },
  tambo: {
    borra: "Solo los tambos importados desde el origen.",
    reimporta: "Tambo.",
  },
})

const BORRA_LIMPIEZA_COMPLETA =
  "Toda la base de negocio del ambiente, también lo que no viene de la importación."

// Con limpieza completa se borra TODO (solo con Productores): el detalle por nivel
// dice qué se vuelve a importar y qué se conserva.
const detalleLimpiezaCompleta = (lista, excluirArca) => ({
  filas: lista.map(nivel => ({
    nivel,
    etiqueta: ETIQUETAS_NIVEL[nivel],
    borra:
      nivel === "productores"
        ? BORRA_LIMPIEZA_COMPLETA
        : "Incluido en la limpieza completa.",
    reimporta:
      nivel === "productores"
        ? excluirArca
          ? "Productores (sin consultar ARCA)."
          : "Productores y su sincronización con ARCA."
        : nivel === "establecimientos"
          ? "Establecimientos y «Sin Detalle»."
          : `${ETIQUETAS_NIVEL[nivel]}.`,
  })),
  noToca: "Usuarios, configuración, dominios y auditoría.",
  avisoManual: false,
  avisoLotes: AVISO_LOTES_EDITADOS,
})

/**
 * Detalle por nivel de la selección efectiva: qué se borra, qué se reimporta y
 * qué no se toca. Devuelve { filas: [{nivel, etiqueta, borra, reimporta}],
 * noToca, avisoManual }.
 */
export const detalleNiveles = (
  efectiva,
  { excluirArca = false, limpiezaCompleta = false } = {},
) => {
  const lista = Array.isArray(efectiva) ? efectiva : []
  const raiz = raizDe(lista)
  if (limpiezaCompleta) return detalleLimpiezaCompleta(lista, excluirArca)
  const filas = []
  if (lista.includes("productores")) {
    filas.push({
      nivel: "productores",
      etiqueta: ETIQUETAS_NIVEL.productores,
      borra:
        "Los productores migrados (los que vienen del sistema de origen) y todo lo que depende de ellos.",
      reimporta: excluirArca
        ? "Productores (sin consultar ARCA)."
        : "Productores y su sincronización con ARCA.",
    })
  }
  if (lista.includes("establecimientos")) {
    filas.push({
      nivel: "establecimientos",
      etiqueta: ETIQUETAS_NIVEL.establecimientos,
      borra:
        "Los establecimientos migrados (incluye «Sin Detalle»), su vínculo con el origen y todo lo que depende de ellos.",
      reimporta: "Establecimientos y «Sin Detalle».",
    })
  }
  ACTIVIDADES.filter(a => lista.includes(a)).forEach(a => {
    const sola = raiz === "actividades"
    filas.push({
      nivel: a,
      etiqueta: ETIQUETAS_NIVEL[a],
      borra: sola
        ? TEXTO_ACTIVIDAD[a].borra
        : "Se borra junto con los establecimientos que dependen de lo migrado.",
      reimporta: TEXTO_ACTIVIDAD[a].reimporta,
    })
  })
  let noToca = ""
  if (raiz === "productores") {
    noToca =
      "Campañas, dominios y lo cargado a mano que no cuelga de lo migrado."
  } else if (raiz === "establecimientos") {
    noToca = "Productores, ARCA, campañas y dominios."
  } else if (raiz === "actividades") {
    const otras = ACTIVIDADES.filter(a => !lista.includes(a))
    noToca = `Productores, establecimientos y su vínculo con el origen, «Sin Detalle», campañas, dominios, lo cargado a mano${
      otras.length
        ? ` y las demás actividades (${otras
            .map(a => ETIQUETAS_NIVEL[a])
            .join(", ")})`
        : ""
    }.`
  }
  return {
    filas,
    noToca,
    avisoManual: raiz === "productores" || raiz === "establecimientos",
    // BDSJEA-462: reimportar establecimientos recrea los lotes (se pierden las ediciones); las actividades solas no los tocan
    avisoLotes:
      raiz === "productores" || raiz === "establecimientos"
        ? AVISO_LOTES_EDITADOS
        : raiz === "actividades"
          ? AVISO_LOTES_ACTIVIDADES
          : "",
  }
}

// Contadores de la última importación por nivel (contrato 7.4): creados más lo
// que "ya existía" (saltados o yaExistian según el paso).
const CAMPO_YA_EXISTIAN = Object.freeze({
  productores: "yaExistian",
  establecimientos: "saltados",
  siembras: "saltados",
  ganaderia: "yaExistian",
  tambo: "saltados",
})

const esNumero = n => typeof n === "number" && Number.isFinite(n)

/**
 * Cuántas filas cargó la última importación del nivel, tomado del estado de la
 * corrida previa (sin endpoint nuevo, S4). null si no hay dato: no se inventa.
 */
export const conteoUltimaCarga = (estadoPrevio, nivel) => {
  const pasos = Array.isArray(estadoPrevio?.pasos) ? estadoPrevio.pasos : []
  const paso = pasos.find(p => p?.id === `importar_${nivel}`)
  const c = paso?.contadores
  if (!c || typeof c !== "object" || !esNumero(c.creados)) return null
  const ya = c[CAMPO_YA_EXISTIAN[nivel]]
  return c.creados + (esNumero(ya) ? ya : 0)
}
